import { BaseRecord } from '../utils/storage/base';

export const NEVER_CONST_REP = '0 days';

export interface SuggestedDosage extends BaseRecord {
  name: string; // Name of the substance
  type: RegimenType; // Type of the substance
  amount?: number; // Amount for dosage preparation
  amount_units?: string; // Units for the amount (e.g., mg, ml)
  reconstitution_amount?: number; // Amount of liquid used for reconstitution
  reconstitution_units?: DosageUnit; // Units for reconstitution (e.g., ml)
  syringe_units_per_dose?: number; // Syringe units per dose
  dosage?: number; // Recommended dosage per administration
  dosage_units?: DosageUnit; // Units for the dosage (e.g., mg, mcg)
  timing: TimingOption; // Recommended timing
  doses_per_interval?: number;
  frequency_interval?: string; // Time between doses (e.g., '1 day', '2 days')
  days_on?: number; // Number of active days in a cycle
  days_off?: number; // Number of rest days in a cycle
  duration_period?: string; // Total active duration (e.g., '8 weeks')
  duration_repeat?: string; // Cooldown or repeat duration (e.g., '8 weeks')
  dosage_level?: DosageLevel;
  dosage_type?: DosageType;
  notes?: string;
  source?: string;
}
export interface Regimen extends BaseRecord {
  name: string; // Name of the regimen
  type: RegimenType; // Regimen type
  dosage?: number; // Optional: Dosage per administration
  dosage_units?: DosageUnit; 
  timing: TimingOption; // Timing for the regimen
  frequency_interval: string; // Time between doses (e.g., '1 day', '3 days')
  doses_per_interval?: number;
  days_on?: number; // Optional: Days on in a cycle
  days_off?: number; // Optional: Days off in a cycle
  duration_period: string; // Total active duration (e.g., '8 weeks')
  duration_repeat?: string; // Optional: Cooldown or repeat period (default: '0 days')
  dosage_level?: DosageLevel;
  dosage_type?: DosageType;
  notes?: string;
  source?: string;
}

export interface IntakeRecord extends BaseRecord {
  name: string; // Name of the regimen or substance
  dosage_taken?: number; // Optional: Dosage taken
  dosage_units?: string; // Optional: Units for the dosage (e.g., mg, mcg)
  intake_time?: string; // Timestamp of intake (optional for inserts)
}

export interface CurrentPlan extends BaseRecord {
  name: string; // Name of the regimen
  type: RegimenType; // Regimen type
  dosage?: number; // Optional: Dosage per administration
  dosage_units?: DosageUnit; 
  timing: TimingOption; // Timing for the regimen
  frequency_interval: string; // Time between doses (e.g., '1 day', '3 days')
  doses_per_interval?: number;
  days_on?: number; // Optional: Days on in a cycle
  days_off?: number; // Optional: Days off in a cycle
  duration_period: string; // Total active duration (e.g., '8 weeks')
  duration_repeat?: string; // Optional: Cooldown or repeat period (default: '0 days')
  dosage_level?: DosageLevel;
  dosage_type?: DosageType;
  notes?: string;
  source?: string;
  start_date: string; // Start date for the plan (e.g., '2024-12-01')
  enabled: boolean; // Indicates the plan is enabled, even if the start date is in the future
}

export type DosageType = typeof DOSAGE_TYPES[number];
export type DosageLevel = typeof DOSAGE_LEVELS[number];
export type RegimenType = typeof REGIMEN_TYPES[number];
export type TimingOption = typeof TIMING_OPTIONS[number];
export type DosageUnit = typeof DOSAGE_UNITS[number];


export const DOSAGE_LEVELS = [
  'low',
  'standard',
  'high',
  'unknown'
] as const;

export const DOSAGE_TYPES = [
  'baseline',
  'loading',
  'therapeutic',
  'booster',
  'maintenance',
  'unknown'
] as const;

export const REGIMEN_TYPES = [
  'peptide',
  'coenzyme',
  'amino',
  'supplement',
  'food',
  'drink',
  'medication',
  'herbal',
  'activity',
  'other'
] as const;

export const TIMING_OPTIONS = [
  'AM',
  'PM',
  'AM before exercise',
  'midday',
  'AM/PM',
  'N/A',
  'before bed',
  'anytime'
] as const;

export const DOSAGE_UNITS = [
  'mg',
  'mcg',
  'g',
  'ml',
  'pills',
  'units'
] as const;

export interface DoseRequiredRecord extends BaseRecord {
  name: string;
  totalAmount: number;
  totalAmountUnits: string;
  totalDoses: number;
  startDate: string;
  endDate: string;
}